import React, { useEffect, useState, useCallback } from "react";
import { Search, X, ShieldCheck, Sparkles, Heart, Ban, RotateCcw, Crown, UserPlus, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";
import { NewAccountModal, CopyLink } from "@/components/admin/NewAccountModal";

const PLANS = ["free", "lite", "plus", "premium", "luxe"];
const PLAN_LABEL = { free: "Free", lite: "Lite", plus: "Plus", premium: "Premium", luxe: "Luxe", sense_plus: "Sense+" };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const fmtAgo = (s) => {
  if (!s) return "never";
  const d = (Date.now() - new Date(s).getTime()) / 86400000;
  if (d < 1) return "today"; if (d < 2) return "yesterday"; if (d < 30) return `${Math.floor(d)}d ago`;
  return fmtDate(s);
};
const cell = "px-3 py-2.5 text-sm text-cocoa align-middle";
const input = "bg-ivory border border-champagne rounded-md h-10 px-3 text-sm text-cocoa outline-none focus:border-mauve";

function StatusChip({ u }) {
  if (u.is_disabled) return <span className="text-rose text-xs font-medium">Deactivated</span>;
  if (u.account_status === "pending") return <span className="text-gold text-xs font-medium" data-testid={`member-pending-${u.id}`}>Pending password</span>;
  return <span className="text-sage text-xs font-medium">{u.subscription_status === "active" ? "Active" : "Member"}</span>;
}

function PlanChip({ plan, override, testId }) {
  const tone = plan === "free" || !plan ? "bg-champagne/60 text-cocoa/70" : plan === "luxe" ? "bg-cocoa text-ivory" : "bg-gold/25 text-cocoa";
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>
      {PLAN_LABEL[plan] || plan || "Free"}{override && <Crown size={11} className="text-gold" title="Manual override" />}
    </span>
  );
}

export function AdminMembers() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [plan, setPlan] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
      if (q.trim()) params.set("q", q.trim());
      if (filter === "beta") params.set("only_beta", "true");
      if (filter === "admins") params.set("only_admins", "true");
      if (filter === "ambassadors") params.set("only_ambassadors", "true");
      if (filter === "disabled") params.set("only_disabled", "true");
      if (filter === "pending") params.set("only_pending", "true");
      if (plan) params.set("plan", plan);
      const r = await api.get(`/admin/users?${params}`);
      setData(r.data);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setLoading(false); }
  }, [q, filter, plan, skip]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / limit));
  const page = Math.floor(skip / limit) + 1;

  return (
    <div data-testid="admin-members">
      <div className="ss-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-3 text-cocoa/40" />
          <input data-testid="members-search" value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Search email, name or alias…" className={`${input} w-full pl-9`} />
        </div>
        <select data-testid="members-filter" value={filter} onChange={(e) => { setSkip(0); setFilter(e.target.value); }} className={input}>
          <option value="all">Everyone</option><option value="admins">Admins</option><option value="beta">Beta / free-forever</option>
          <option value="ambassadors">Ambassadors</option><option value="disabled">Deactivated</option><option value="pending">Pending password</option>
        </select>
        <select data-testid="members-plan-filter" value={plan} onChange={(e) => { setSkip(0); setPlan(e.target.value); }} className={input}>
          <option value="">Any plan</option>{PLANS.map((p) => <option key={p} value={p}>{PLAN_LABEL[p]}</option>)}<option value="sense_plus">Sense+ (legacy key)</option>
        </select>
        <span className="text-xs text-cocoa/55" data-testid="members-total">{data.total.toLocaleString()} members</span>
        <button onClick={() => setCreating(true)} className="ss-pill ss-pill-primary h-10 ml-auto" data-testid="members-new-account"><UserPlus size={14} /> New account</button>
      </div>
      {creating && <NewAccountModal plans={PLANS} planLabel={PLAN_LABEL} onClose={() => setCreating(false)} onCreated={load} />}

      {/* Phones: one card per member — same data, no sideways scrolling */}
      <div className="mt-4 space-y-2 sm:hidden" data-testid="members-cards">
        {data.items.map((u) => (
          <button type="button" key={u.id} data-testid={`member-card-${u.id}`} onClick={() => setSelected(u.id)}
            className={`w-full text-left ss-card p-4 active:scale-[0.99] transition-transform ${u.is_disabled ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-cocoa truncate">{u.display_name || u.name || "—"}</p>
                <p className="text-xs text-cocoa/55 truncate">{u.email}</p>
              </div>
              <PlanChip plan={u.plan} override={!!u.plan_override} />
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-cocoa/65">
              <span><StatusChip u={u} /> · joined {fmtDate(u.created_at)}</span>
              <span>{fmtAgo(u.last_active_at)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-cocoa/65">
              <span>{u.advisor_sessions} reads · {u.profiles} profiles</span>
              <span className="flex gap-1.5">
                {u.role === "admin" && <ShieldCheck size={14} title="Admin" className="text-mauve" />}
                {u.beta_forever && <Sparkles size={14} title="Beta / free-forever" className="text-gold" />}
                {u.is_ambassador && <Heart size={14} title="Ambassador" className="text-rose" />}
              </span>
            </div>
          </button>
        ))}
        {!loading && data.items.length === 0 && <p className="ss-card p-6 text-center text-sm text-cocoa/50">No members match.</p>}
        <div className="flex items-center justify-between px-1 py-2 text-xs text-cocoa/60">
          <span>{loading ? "Loading…" : `Page ${page} of ${pages}`}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40">Prev</button>
            <button disabled={page >= pages} onClick={() => setSkip(skip + limit)} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>

      <div className="ss-card mt-4 hidden sm:block overflow-x-auto">
        <table className="w-full text-left" data-testid="members-table">
          <thead className="text-[10px] uppercase tracking-[0.25em] text-cocoa/50 border-b border-champagne">
            <tr>{["Member", "Plan", "Status", "Joined", "Last active", "Reads", "Profiles", "Flags"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.items.map((u) => (
              <tr key={u.id} data-testid={`member-row-${u.id}`} onClick={() => setSelected(u.id)} className={`border-b border-champagne/60 cursor-pointer hover:bg-champagne/30 ${u.is_disabled ? "opacity-60" : ""}`}>
                <td className={cell}><div className="font-medium">{u.display_name || u.name || "—"}</div><div className="text-xs text-cocoa/55">{u.email}</div></td>
                <td className={cell}><PlanChip plan={u.plan} override={!!u.plan_override} /></td>
                <td className={cell}><StatusChip u={u} /></td>
                <td className={cell}>{fmtDate(u.created_at)}</td>
                <td className={cell}>{fmtAgo(u.last_active_at)}</td>
                <td className={cell}>{u.advisor_sessions}</td>
                <td className={cell}>{u.profiles}</td>
                <td className={cell}><div className="flex gap-1.5 text-cocoa/60">
                  {u.role === "admin" && <ShieldCheck size={14} title="Admin" className="text-mauve" />}
                  {u.beta_forever && <Sparkles size={14} title="Beta / free-forever" className="text-gold" />}
                  {u.is_ambassador && <Heart size={14} title="Ambassador" className="text-rose" />}
                </div></td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-cocoa/50">No members match.</td></tr>}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-3 py-2.5 text-xs text-cocoa/60 border-t border-champagne/60">
          <span>{loading ? "Loading…" : `Page ${page} of ${pages}`}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40" data-testid="members-prev">Prev</button>
            <button disabled={page >= pages} onClick={() => setSkip(skip + limit)} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40" data-testid="members-next">Next</button>
          </div>
        </div>
      </div>

      {selected && <MemberDrawer userId={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return <div className="bg-ivory rounded-lg p-3 border border-champagne/70"><p className="text-[10px] uppercase tracking-widest text-cocoa/50">{label}</p><p className="mt-0.5 font-heading text-xl text-cocoa" data-testid={testId}>{value ?? "—"}</p></div>;
}

function MemberDrawer({ userId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("free");
  const [expiry, setExpiry] = useState("");
  const [note, setNote] = useState("");
  const [link, setLink] = useState(null);

  const regenLink = () => act(async () => { const r = await api.post(`/admin/users/${userId}/activation-link`); setLink(r.data); }, "Fresh set-password link generated");

  const load = useCallback(() => api.get(`/admin/users/${userId}/detail`).then((r) => { setD(r.data); setPlan(r.data.user.plan_override?.plan || (PLANS.includes(r.data.user.plan) ? r.data.user.plan : "plus")); }).catch((e) => toast.error(getErrorMessage(e))), [userId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); onChanged(); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const u = d?.user;
  const setPlanNow = () => act(() => api.put(`/admin/users/${userId}/plan`, { plan, expires_in_days: expiry ? Number(expiry) : null, note: note || null }), `Plan set to ${PLAN_LABEL[plan]}`);
  const toggle = (path, body, ok) => act(() => api.put(`/admin/users/${userId}/${path}`, body), ok);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="member-drawer">
      <div className="absolute inset-0 bg-cocoa/30 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative w-full sm:max-w-xl h-full bg-ivory shadow-2xl overflow-y-auto p-4 sm:p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Member</p>
            <h3 className="font-heading text-3xl text-cocoa" data-testid="member-drawer-name">{u?.display_name || u?.name || "…"}</h3>
            <p className="text-sm text-cocoa/60" data-testid="member-drawer-email">{u?.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-champagne" data-testid="member-drawer-close" aria-label="Close"><X size={18} /></button>
        </div>
        {!d ? <p className="text-sm text-cocoa/55">Loading…</p> : (
          <>
            <div className="flex flex-wrap gap-2 items-center">
              <PlanChip plan={u.plan} override={!!u.plan_override} testId="member-plan-chip" />
              {u.role === "admin" && <span className="px-2 py-0.5 rounded-full text-xs bg-mauve/15 text-mauve">Admin</span>}
              {u.beta_forever && <span className="px-2 py-0.5 rounded-full text-xs bg-gold/25 text-cocoa">Beta / free-forever</span>}
              {u.is_ambassador && <span className="px-2 py-0.5 rounded-full text-xs bg-rose/20 text-cocoa">Ambassador</span>}
              {u.is_disabled && <span className="px-2 py-0.5 rounded-full text-xs bg-rose text-ivory" data-testid="member-disabled-chip">Deactivated</span>}
              {u.account_status === "pending" && <span className="px-2 py-0.5 rounded-full text-xs bg-gold/30 text-cocoa" data-testid="member-pending-chip">Pending password</span>}
            </div>
            {u.account_status === "pending" && (
              <div className="ss-card p-4 space-y-3 border border-gold/40" data-testid="member-pending-panel">
                <p className="text-sm text-cocoa/75">This account was created by an admin and hasn't set a password yet. Generate a fresh link if the original was lost or expired (it invalidates earlier links).</p>
                {link ? <CopyLink link={link.link} expiresAt={link.expires_at} testId="member-activation-link" />
                  : <button disabled={busy} onClick={regenLink} className="ss-pill ss-pill-gold h-10" data-testid="member-regenerate-link"><Link2 size={14} /> Generate new set-password link</button>}
              </div>
            )}
            {u.plan_override && (
              <p className="text-xs text-cocoa/65 bg-gold/10 border border-gold/30 rounded-md p-2.5" data-testid="member-override-note">
                Manual override to <strong>{PLAN_LABEL[u.plan_override.plan]}</strong> by {u.plan_override.set_by_email || "admin"} on {fmtDate(u.plan_override.set_at)}
                {u.plan_override.expires_at ? ` · reverts to ${PLAN_LABEL[u.plan_override.previous_plan] || u.plan_override.previous_plan} on ${fmtDate(u.plan_override.expires_at)}` : " · indefinite"}
                {u.plan_override.note ? ` · “${u.plan_override.note}”` : ""}
              </p>
            )}

            <div className="grid grid-cols-3 gap-2">
              <Stat label="Joined" value={fmtDate(u.created_at)} />
              <Stat label="Last login" value={fmtAgo(d.last_login_at)} />
              <Stat label="Calibrated" value={u.has_calibrated ? "Yes" : "No"} />
              <Stat label="Advisor reads" value={d.counts.advisor_sessions} testId="member-stat-reads" />
              <Stat label="Reads · 30d" value={d.counts.advisor_sessions_30d} />
              <Stat label="Mentor sessions" value={d.counts.mentor_sessions} />
              <Stat label="Profiles" value={`${d.counts.profiles} / ${d.entitlements.max_profiles}`} />
              <Stat label="Memory vectors" value={d.counts.embeddings} />
              <Stat label="AI spend · 30d" value={`$${d.ai_cost_usd_30d.toFixed(3)}`} />
            </div>

            {d.usage?.units && (
              <div className="ss-card p-4">
                <p className="text-[10px] uppercase tracking-widest text-cocoa/50">This month on {d.entitlements.name}</p>
                <div className="mt-2 h-2 rounded-full bg-champagne overflow-hidden"><div className="h-full bg-gold" style={{ width: `${Math.min(100, (100 * (d.usage.units.used || 0)) / Math.max(1, d.usage.units.limit || 1))}%` }} /></div>
                <p className="mt-1.5 text-xs text-cocoa/65">{d.usage.units.used ?? 0} of {d.usage.units.limit} AI units · {d.entitlements.ai_insights_per_month} insights · {d.entitlements.deep_memory_enabled ? "deep memory on" : "basic memory"}{d.entitlements.priority_processing ? " · priority" : ""}</p>
              </div>
            )}

            <div className="ss-card p-4 space-y-3" data-testid="member-plan-panel">
              <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Set plan manually</p>
              <div className="flex flex-wrap gap-2">
                <select data-testid="member-plan-select" value={plan} onChange={(e) => setPlan(e.target.value)} className={input}>{PLANS.map((p) => <option key={p} value={p}>{PLAN_LABEL[p]}</option>)}</select>
                <select data-testid="member-plan-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)} className={input}>
                  <option value="">Indefinite (until changed)</option><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option><option value="365">1 year</option>
                </select>
              </div>
              <input data-testid="member-plan-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Internal note (e.g. press trial, tester, goodwill)" className={`${input} w-full`} />
              <button disabled={busy} onClick={setPlanNow} className="ss-pill ss-pill-primary h-10 disabled:opacity-50" data-testid="member-plan-save">Apply plan</button>
            </div>

            <div className="ss-card p-4 grid sm:grid-cols-2 gap-2" data-testid="member-flags-panel">
              <button disabled={busy} onClick={() => toggle("beta", { beta_forever: !u.beta_forever }, u.beta_forever ? "Beta removed" : "Beta granted")} className="ss-pill ss-pill-ghost h-10 justify-start" data-testid="member-toggle-beta"><Sparkles size={14} /> {u.beta_forever ? "Remove beta / free-forever" : "Grant beta / free-forever"}</button>
              <button disabled={busy} onClick={() => toggle("ambassador", { is_ambassador: !u.is_ambassador }, u.is_ambassador ? "Ambassador removed" : "Ambassador flagged")} className="ss-pill ss-pill-ghost h-10 justify-start" data-testid="member-toggle-ambassador"><Heart size={14} /> {u.is_ambassador ? "Remove ambassador flag" : "Flag as ambassador"}</button>
              <button disabled={busy} onClick={() => { if (window.confirm(`${u.role === "admin" ? "Remove admin from" : "Make admin:"} ${u.email}?`)) toggle("role", { role: u.role === "admin" ? "user" : "admin" }, "Role updated"); }} className="ss-pill ss-pill-ghost h-10 justify-start" data-testid="member-toggle-role"><ShieldCheck size={14} /> {u.role === "admin" ? "Remove admin role" : "Grant admin role"}</button>
              {u.is_disabled
                ? <button disabled={busy} onClick={() => toggle("active", { is_active: true }, "Account reactivated")} className="ss-pill ss-pill-gold h-10 justify-start" data-testid="member-reactivate"><RotateCcw size={14} /> Reactivate account</button>
                : <button disabled={busy} onClick={() => { const r = window.prompt("Reason for deactivating (internal):", ""); if (r !== null) toggle("active", { is_active: false, reason: r }, "Account deactivated"); }} className="ss-pill ss-pill-ghost h-10 justify-start text-rose" data-testid="member-deactivate"><Ban size={14} /> Deactivate account</button>}
            </div>

            {d.billing && (
              <div className="ss-card p-4 text-sm text-cocoa/75" data-testid="member-billing">
                <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Billing</p>
                <p className="mt-1">{d.billing.sku || d.billing.plan || "—"} · {d.billing.status || "—"}{d.billing.current_period_end ? ` · renews ${fmtDate(d.billing.current_period_end)}` : ""}</p>
              </div>
            )}
            {d.ontology && (
              <div className="ss-card p-4 text-sm text-cocoa/75">
                <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Ontology state (system signal only)</p>
                <p className="mt-1">Primary archetype: <strong>{(d.ontology.primary_archetype_key || "—").replace(/_/g, " ")}</strong>{d.ontology.confidence != null ? ` · confidence ${Math.round(d.ontology.confidence * 100)}%` : ""}</p>
              </div>
            )}
            {d.audit?.length > 0 && (
              <div className="ss-card p-4" data-testid="member-audit">
                <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Admin actions on this account</p>
                <ul className="mt-2 space-y-1 text-xs text-cocoa/70">
                  {d.audit.map((a, i) => <li key={i}>{fmtDate(a.ts || a.at)} · {a.action.replace("admin.", "").replace(/_/g, " ")}{a.meta?.to ? ` → ${a.meta.to}` : ""}{a.meta?.note ? ` · ${a.meta.note}` : ""}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
